import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store, AppDispatch, RootState } from './store';
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Wishlist from './pages/Wishlist';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import AccountProfile from './pages/Account/Profile';
import AccountOrders from './pages/Account/Orders';
import AccountAddresses from './pages/Account/Addresses';
import AccountBusinessDetails from './pages/Account/BusinessDetails';
import AccountRFQs from './pages/Account/RFQs';
import AccountWishlist from './pages/Account/Wishlist';
import Search from './pages/Search';
import TrackOrder from './pages/TrackOrder';
import About from './pages/About';
import Contact from './pages/Contact';
import Help from './pages/Help';
import Press from './pages/Press';
import Testimonials from './pages/Testimonials';
import Blogs from './pages/Blogs';
import BlogDetail from './pages/BlogDetail';
import Articles from './pages/Articles';
import ArticleDetail from './pages/ArticleDetail';
import FAQ from './pages/FAQ';
import Cancellation from './pages/Cancellation';
import Refund from './pages/Refund';
import PaymentOptions from './pages/PaymentOptions';
import Terms from './pages/Terms';
import PopularSearch from './pages/PopularSearch';
import BestDeal from './pages/BestDeal';
import OnlineAssist from './pages/OnlineAssist';
import IndustryStore from './pages/IndustryStore';
import B2BBuyers from './pages/B2BBuyers';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/common/ProtectedRoute';
import HomeSecurityLanding from './pages/HomeSecurity/HomeSecurityLanding';
import StaticPage from './pages/StaticPage';
import AdminPanel from './pages/Admin/AdminPanel';
import { registerAuthFailureCallback } from './api/apiClient';
import { logout } from './store/slices/authSlice';
import { fetchProducts, fetchCategories, fetchMaintenanceMode, refreshAccessToken } from './store/thunks';
import './styles/globals.css';
import './styles/responsive.css';

// Guard component: only allows ADMIN / SUPER_ADMIN roles
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const adminRoles = ['SUPER_ADMIN', 'ADMIN', 'INVENTORY_MANAGER'];
  if (!isAuthenticated) return <Navigate to="/auth/login" state={{ from: { pathname: '/admin' } }} replace />;
  if (!user || !adminRoles.includes(user.role as string)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Inner component that has access to dispatch after Provider
function AppBootstrap({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // Register the callback to cleanly log out when refresh token fails
    registerAuthFailureCallback(() => {
      dispatch(logout());
    });

    // Attempt silent token refresh first to restore session from HttpOnly cookie
    dispatch(refreshAccessToken()).finally(() => {
      dispatch(fetchProducts({ limit: 1000 }));
      dispatch(fetchCategories());
      dispatch(fetchMaintenanceMode());
    });
  }, [dispatch]);

  return <>{children}</>;
}


function App() {
  return (
    <Provider store={store}>
      <Router>
        <AppBootstrap>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Home />} />
              <Route path="shop" element={<Shop />} />
              <Route path="shop/:category" element={<Shop />} />
              <Route path="shop/:category/:subcategory" element={<Shop />} />
              <Route path=":category" element={<Shop />} />
              <Route path=":category/:subcategory" element={<Shop />} />
              <Route path="product/:id" element={<ProductDetail />} />
              <Route path="cart" element={<Cart />} />
              <Route path="wishlist" element={<Wishlist />} />
              <Route path="search" element={<Search />} />
              <Route path="track/:orderId" element={<TrackOrder />} />
              <Route path="about" element={<About />} />
              <Route path="contact" element={<Contact />} />
              <Route path="help" element={<Help />} />
              <Route path="press" element={<Press />} />
              <Route path="testimonials" element={<Testimonials />} />
              <Route path="blogs" element={<Blogs />} />
              <Route path="blogs/:id" element={<BlogDetail />} />
              <Route path="articles" element={<Articles />} />
              <Route path="articles/:id" element={<ArticleDetail />} />
              <Route path="contact" element={<Contact />} />
              <Route path="faq" element={<FAQ />} />
              <Route path="cancellation" element={<Cancellation />} />
              <Route path="refund" element={<Refund />} />
              <Route path="payment-options" element={<PaymentOptions />} />
              <Route path="terms" element={<Terms />} />
              <Route path="popular-search" element={<PopularSearch />} />
              <Route path="best-deal" element={<BestDeal />} />
              <Route path="home-security" element={<HomeSecurityLanding />} />
              <Route path="industry-store" element={<IndustryStore />} />
              <Route path="category/:category" element={<Shop />} />
              <Route path="find-b2b-buyers" element={<B2BBuyers />} />

              {/* Missing Static Pages */}
              <Route path="buying-guide" element={<StaticPage />} />
              <Route path="top-buying" element={<StaticPage />} />
              <Route path="copyright" element={<StaticPage />} />
              <Route path="privacy" element={<StaticPage />} />
              <Route path="compliance" element={<StaticPage />} />
            </Route>

            {/* Online Assist — full-page loader then redirect home */}
            <Route path="online-assist" element={<OnlineAssist />} />
            <Route path="online-assist/login" element={<OnlineAssist />} />

            {/* Auth Routes */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route path="login" element={<Login />} />
              <Route path="register" element={<Register />} />
            </Route>

            {/* Protected Routes - Checkout */}
            <Route
              path="/checkout"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Checkout />} />
            </Route>

            {/* Protected Account Routes */}
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AccountProfile />} />
              <Route path="profile" element={<AccountProfile />} />
              <Route path="orders" element={<AccountOrders />} />
              <Route path="addresses" element={<AccountAddresses />} />
              <Route path="business" element={<AccountBusinessDetails />} />
              <Route path="rfqs" element={<AccountRFQs />} />
              <Route path="wishlist" element={<AccountWishlist />} />
            </Route>

            {/* Admin Panel – role-guarded */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              }
            />
            <Route path="/admin/*" element={<Navigate to="/admin" replace />} />

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppBootstrap>
      </Router>
    </Provider>
  );
}

export default App;
